import { create } from "zustand";

export type MotionSetting = "on" | "off" | "system";

const STORAGE_KEY = "enex.motion";

/**
 * Set `<html data-motion="off">` from the profile switch and from the system setting
 * (`prefers-reduced-motion`). With "system", the system setting decides.
 */
export function applyMotion(
  setting: MotionSetting = "system",
  doc: Document = document,
  matchMedia: ((query: string) => { matches: boolean }) | undefined = typeof window === "undefined" ? undefined : window.matchMedia?.bind(window),
): boolean {
  const reduced = matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  const off = setting === "off" || (setting === "system" && reduced);
  if (off) doc.documentElement.setAttribute("data-motion", "off");
  else doc.documentElement.removeAttribute("data-motion");
  return off;
}

function isSetting(value: unknown): value is MotionSetting {
  return value === "on" || value === "off" || value === "system";
}

/** The saved choice of this browser. Storage can be missing or blocked: then the system setting decides. */
export function readMotionSetting(): MotionSetting {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isSetting(value) ? value : "system";
  } catch {
    return "system";
  }
}

function saveMotionSetting(setting: MotionSetting): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, setting);
  } catch {
    // The choice then lasts until the page is closed.
  }
}

interface MotionState {
  setting: MotionSetting;
  /** Choose, apply and remember. The profile screen and the top bar both call this. */
  choose(setting: MotionSetting): void;
}

export const useMotionStore = create<MotionState>((set) => ({
  setting: "system",
  choose: (setting) => {
    set({ setting });
    saveMotionSetting(setting);
    applyMotion(setting);
  },
}));

/** Start-up: read the saved choice, apply it, and follow the system setting while it is "system". Returns a function that stops following. */
export function initMotion(): () => void {
  const setting = readMotionSetting();
  useMotionStore.setState({ setting });
  applyMotion(setting);
  const query = typeof window !== "undefined" ? window.matchMedia?.("(prefers-reduced-motion: reduce)") : undefined;
  if (!query?.addEventListener) return () => undefined;
  const onChange = () => {
    if (useMotionStore.getState().setting === "system") applyMotion("system");
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
